/**
 * Feature Flags for Multi-Job System Enhancement
 *
 * These flags control the rollout of the new multi-job system enhancements.
 * All flags start as false to ensure no impact on existing functionality.
 * Enable flags gradually as each phase is implemented and tested.
 */

export const FEATURE_FLAGS = {
    /**
     * Phase 2: Database Queue System
     * Enables serialized database operations to prevent corruption
     */
    USE_DATABASE_QUEUE: true,

    /**
     * Phase 3: Centralized Job Manager - DISABLED (ROLLBACK)
     * Enables single polling loop and coordinated job management
     * ROLLED BACK: Causing timing issues with job polling
     */
    USE_NEW_JOB_MANAGER: false,

    /**
     * Phase 4: Database Health Monitoring
     * Enables corruption detection and automatic recovery
     */
    USE_HEALTH_MONITOR: true,

    /**
     * Phase 5: Enhanced Job Coordination
     * Enables coordination between undelivered image recovery and active jobs
     */
    USE_JOB_COORDINATION: false,

    /**
     * Development and Debugging
     */
    ENABLE_DEBUG_LOGGING: true,
    ENABLE_PERFORMANCE_MONITORING: false,
    ENABLE_STATE_VALIDATION: false,

    /**
     * Safety and Recovery
     */
    ENABLE_AUTOMATIC_RECOVERY: false,
    ENABLE_CORRUPTION_ALERTS: false,
    ENABLE_BACKUP_ON_CORRUPTION: false
};

/**
 * Helper function to check if a feature is enabled
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (flagName) => {
    return FEATURE_FLAGS[flagName] === true;
};

/**
 * Helper function to enable a feature flag
 * @param {string} flagName - Name of the feature flag
 */
export const enableFeature = (flagName) => {
    if (flagName in FEATURE_FLAGS) {
        FEATURE_FLAGS[flagName] = true;
        console.log(`[FEATURE_FLAGS] Enabled: ${flagName}`);
    } else {
        console.warn(`[FEATURE_FLAGS] Unknown flag: ${flagName}`);
    }
};

/**
 * Helper function to disable a feature flag
 * @param {string} flagName - Name of the feature flag
 */
export const disableFeature = (flagName) => {
    if (flagName in FEATURE_FLAGS) {
        FEATURE_FLAGS[flagName] = false;
        console.log(`[FEATURE_FLAGS] Disabled: ${flagName}`);
    } else {
        console.warn(`[FEATURE_FLAGS] Unknown flag: ${flagName}`);
    }
};

/**
 * Get all feature flags status
 * @returns {object} - Object containing all feature flags and their status
 */
export const getAllFeatureFlags = () => {
    return { ...FEATURE_FLAGS };
};

/**
 * Debug helper to log current feature flag status
 */
export const logFeatureFlags = () => {
    console.log('[FEATURE_FLAGS] Current status:', FEATURE_FLAGS);
};

// Development helper - expose to window in development
if (process.env.NODE_ENV === 'development') {
    window.FEATURE_FLAGS = FEATURE_FLAGS;
    window.enableFeature = enableFeature;
    window.disableFeature = disableFeature;
    window.logFeatureFlags = logFeatureFlags;
}